/**
 * Student login + register screen.
 *
 * Onboarding flow (post-migration b5e8d1a92c34):
 *   - Login:    username + password
 *   - Register: username + password + (school, grade, section, roll_number, first_name)
 *   - Force password reset shown when the server returns must_reset_password=true
 *     (true for legacy accounts backfilled with a temp password by the migration).
 *
 * The wizard / pre-roster UX that the previous Login.tsx had (Ekya JP Nagar
 * pre-rostered list, JNV "Student ID" relabel) was removed because we're on
 * the self-register path for now. When the school-roster path comes back
 * (Option 3 in the design doc), reintroduce that flow as a separate screen.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBackRounded,
  AutoAwesomeRounded,
  LockResetRounded,
  PersonAddRounded,
  LoginRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
import {
  getStudentOptions,
  loginStudent,
  registerStudent,
  resetStudentPassword,
} from '../services/api';
import { StudentOptions } from '../types';

type Mode = 'login' | 'register';

// Sensible defaults if the backend `/config/student-options` call fails
// (e.g. backend cold-starting). The select shows these so the user can still
// register; the server re-validates against student_config.py either way.
const FALLBACK_SCHOOLS = [
  'Ekya School JP Nagar',
  'JNV Bangalore Urban',
  'JNV Palghar',
  'School A',
  'School B',
  'Swarnaayu',
  'Vidyalay Udyam',
];
const FALLBACK_GRADES = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const FALLBACK_SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F'];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const [mode, setMode] = useState<Mode>('login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [forceReset, setForceReset] = useState(false);

  // --- shared fields ---
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // --- register-only fields ---
  const [school, setSchool] = useState('');
  const [grade, setGrade] = useState<number | ''>('');
  const [section, setSection] = useState('');
  const [rollNumber, setRollNumber] = useState<number | ''>('');
  const [firstName, setFirstName] = useState('');

  // --- force-reset screen state ---
  const [currentPasswordForReset, setCurrentPasswordForReset] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // --- canonical student-options (schools / grades / sections) ---
  // Loaded once when the user lands on the Register tab; the backend file
  // backend/src/config/student_config.py is the source of truth. We fall
  // back to the in-file constants if the call fails so registration still
  // works during a backend hiccup.
  const [studentOptions, setStudentOptions] = useState<StudentOptions>({
    schools: FALLBACK_SCHOOLS,
    grades: FALLBACK_GRADES,
    sections: FALLBACK_SECTIONS,
  });

  useEffect(() => {
    if (mode !== 'register') return;
    let mounted = true;
    getStudentOptions()
      .then((opts) => {
        if (!mounted) return;
        setStudentOptions({
          schools: opts.schools?.length ? opts.schools : FALLBACK_SCHOOLS,
          grades: opts.grades?.length ? opts.grades : FALLBACK_GRADES,
          sections: opts.sections?.length ? opts.sections : FALLBACK_SECTIONS,
        });
      })
      .catch(() => {
        // keep fallbacks; user can still register
      });
    return () => {
      mounted = false;
    };
  }, [mode]);

  // ---------- handlers ----------

  const finishAuth = (user: any, token?: string, refreshToken?: string) => {
    login(user, token, refreshToken);
    navigate('/chat', { replace: true });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Username and password are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await loginStudent(username.trim().toLowerCase(), password);
      // Establish the JWT session (access + refresh), then either route to chat
      // or into the forced-reset flow. The reset call below is authenticated
      // and rides on the session we just stored here.
      login(res.user, res.token, res.refresh_token);
      if (res.must_reset_password) {
        // Keep the just-entered password so the reset endpoint can verify it.
        setCurrentPasswordForReset(password);
        setForceReset(true);
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        navigate('/chat', { replace: true });
      }
    } catch (err: any) {
      setError(err?.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // client-side guards (server re-validates)
    if (!username.trim() || username.trim().length < 3) {
      setError('Pick a username with at least 3 characters.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!school.trim() || !firstName.trim() || !grade || !rollNumber) {
      setError('Please fill in your school, grade, roll number and first name.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await registerStudent({
        username: username.trim().toLowerCase(),
        password,
        school: school.trim(),
        grade: Number(grade),
        section: section.trim() ? section.trim().toUpperCase() : null,
        roll_number: Number(rollNumber),
        first_name: firstName.trim(),
      });
      finishAuth(res.user, res.token, res.refresh_token);
    } catch (err: any) {
      setError(err?.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Your new password needs at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords don’t match.');
      return;
    }
    setIsSubmitting(true);
    try {
      await resetStudentPassword(currentPasswordForReset, newPassword);
      // Update BOTH React state and localStorage so ProtectedRoute sees the
      // cleared flag. login(...) writes to localStorage itself. Reading from
      // useAuth's `user` (vs the previous localStorage round-trip) also kills
      // a silent-no-op edge case where storage being cleared between login
      // and force-reset would leave React state stale and bounce the user
      // back to /login. (Original loop caught by Raghav on PR #7.)
      if (user) {
        login({ ...user, must_reset_password: false });
      }
      navigate('/chat', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Could not update your password');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- render ----------

  if (forceReset) {
    return (
      <Shell
        title="Set a new password"
        subtitle="Your account was created with a temporary password. Pick a new one to continue."
        icon={<LockResetRounded fontSize="large" />}
      >
        <form onSubmit={handleForceReset} className="space-y-4">
          <Field
            label="New password"
            type="password"
            value={newPassword}
            onChange={setNewPassword}
            placeholder="at least 6 characters"
            autoFocus
          />
          <Field
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
          />
          {error && <ErrorBanner message={error} />}
          <SubmitButton disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save & continue'}
          </SubmitButton>
        </form>
      </Shell>
    );
  }

  return (
    <Shell
      title={mode === 'login' ? 'Welcome back' : 'Create your account'}
      subtitle={
        mode === 'login'
          ? 'Log in with your username and password.'
          : 'Pick a username + password, and tell us your school details.'
      }
      icon={
        mode === 'login' ? <LoginRounded fontSize="large" /> : <PersonAddRounded fontSize="large" />
      }
    >
      {/* Tab switcher */}
      <div className="flex bg-gray-100 rounded-lg p-1 mb-6 text-sm font-medium">
        <button
          type="button"
          onClick={() => {
            setMode('login');
            setError('');
          }}
          className={`flex-1 py-2 rounded-md transition ${
            mode === 'login'
              ? 'bg-white text-indigo-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Log in
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('register');
            setError('');
          }}
          className={`flex-1 py-2 rounded-md transition ${
            mode === 'register'
              ? 'bg-white text-indigo-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Create account
        </button>
      </div>

      {mode === 'login' ? (
        <form onSubmit={handleLogin} className="space-y-4">
          <Field
            label="Username"
            value={username}
            onChange={setUsername}
            placeholder="e.g. bhumika3"
            autoFocus
            autoCapitalize="off"
            autoComplete="username"
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
          {error && <ErrorBanner message={error} />}
          <SubmitButton disabled={isSubmitting}>
            {isSubmitting ? 'Logging in…' : 'Log in'}
          </SubmitButton>
        </form>
      ) : (
        <form onSubmit={handleRegister} className="space-y-4">
          <Field
            label="Username"
            value={username}
            onChange={setUsername}
            placeholder="3–30 characters: letters, digits, . _ -"
            autoFocus
            autoCapitalize="off"
            autoComplete="username"
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="at least 6 characters"
            autoComplete="new-password"
          />
          <Field
            label="First name"
            value={firstName}
            onChange={setFirstName}
            placeholder="What should we call you?"
          />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">School</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
            >
              <option value="">— pick your school —</option>
              {studentOptions.schools.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Grade</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={grade}
                onChange={(e) => setGrade(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">—</option>
                {studentOptions.grades.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Section</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={section}
                onChange={(e) => setSection(e.target.value)}
              >
                <option value="">—</option>
                {studentOptions.sections.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Roll #</label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={rollNumber}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setRollNumber('');
                    return;
                  }
                  // Strip non-digits and reject negatives / zero / fractions.
                  // The backend enforces MIN_ROLL_NUMBER=1 too, but we prevent
                  // the bad value from ever reaching it.
                  const n = Math.floor(Number(raw));
                  if (!Number.isFinite(n) || n < 1) {
                    setRollNumber('');
                    return;
                  }
                  setRollNumber(n);
                }}
                onKeyDown={(e) => {
                  // Block '-', '+', 'e', 'E' at the keyboard level (they're
                  // valid in <input type=number> by default).
                  if (['-', '+', 'e', 'E'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="42"
              />
            </div>
          </div>
          {error && <ErrorBanner message={error} />}
          <SubmitButton disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create account'}
          </SubmitButton>
        </form>
      )}
    </Shell>
  );
};

// ----- shared visual bits (kept local; private to this screen) ------------

const Shell: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, icon, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="w-full max-w-md">
        {/* Back-to-home control — small, top-left of the card column so it
            stays visible above the brand on every Shell variant (login,
            register, force-reset). */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-indigo-700 transition"
            aria-label="Back to home"
          >
            <ArrowBackRounded fontSize="small" className="mr-1" />
            Back to home
          </button>
        </div>
        <div className="flex items-center justify-center mb-6 text-indigo-600">
          <AutoAwesomeRounded fontSize="large" />
          <span className="ml-2 text-xl font-semibold text-gray-800">Curiosity Coach</span>
        </div>
        <div className="bg-white shadow-lg rounded-2xl p-7 border border-gray-100">
          <div className="flex items-center mb-3 text-indigo-600">{icon}</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-1">{title}</h1>
          <p className="text-sm text-gray-500 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoFocus?: boolean;
  autoCapitalize?: string;
  autoComplete?: string;
  iconLeft?: React.ReactNode;
}> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoFocus,
  autoCapitalize,
  autoComplete,
  iconLeft,
}) => {
  // Password fields get a show/hide eye so students can check what they typed.
  const isPassword = type === 'password';
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        {iconLeft && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            {iconLeft}
          </span>
        )}
        <input
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoCapitalize={autoCapitalize}
          autoComplete={autoComplete}
          className={`w-full border border-gray-300 rounded-md py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
            iconLeft ? 'pl-9' : 'pl-3'
          } ${isPassword ? 'pr-10' : 'pr-3'}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show}
            tabIndex={-1}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            {show ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
          </button>
        )}
      </div>
    </div>
  );
};

const SubmitButton: React.FC<{ disabled?: boolean; children: React.ReactNode }> = ({
  disabled,
  children,
}) => (
  <button
    type="submit"
    disabled={disabled}
    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-md transition"
  >
    {children}
  </button>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
    {message}
  </div>
);

export default Login;
